import { ComponentProps } from '@/components/Internals/ContentComposer';
import { ApplySchema, SchemaType } from '@/components/Internals/InternalSchema';
import Layout from '@/components/Layout';
import Breadcrumb, { BreadcrumbItem } from '@/components/Layout/Breadcrumb';
import WrapperContent from '@/components/Layout/WrapperContent';
import { NavBarContent } from '@/components/Navbar';
import ProfilePage from '@/components/Profile';
import { ProfileLinksI } from '@/components/Profile/ProfileHeader/ProfileLinks';
import { ProfileAdContainer } from '@/components/Profile/styles';
import useBreakpoint from '@/hooks/useBreakpoint';
import { api, mimir, site } from '@/services/api';
import { HeadTags, NavBarContentData, Segmentations, TimeLineApi } from '@/shared/types/home.types';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

const FeaturedLastNews = dynamic(() => import('@/components/Home/FeaturedLastNews'), { ssr: false });
const FeaturedProducts = dynamic(() => import('@/components/Home/FeaturedProducts'), { ssr: false });
const AdsGeneratorMemo = dynamic(() => import('@/components/Ads/Google/AdsTypeGeneratorsMemo'), { ssr: false });

export interface CompanyProfileProps {
  responseData: {
    status: number;
    data: {
      nome: string;
      codigoBolsa?: string;
      imagem: {
        url: string;
        blurhash: string;
      };
      subsidiariaDe?: string;
      fundacao?: {
        data?: string;
        local?: string;
      };
      links: ProfileLinksI['links'];
      texto?: ComponentProps[] | string;
      produtosEmpresa?: {
        titulo: string;
        imagem: {
          url: string;
          blurhash: string;
        };
        url: string;
        rumor: boolean;
      }[];
    };
    ssr: HeadTags[];
    segmentacoes: Segmentations[];
    schema: SchemaType;
    headers: {
      'Cache-control': string;
      Expires: string;
    };
    breadcrumb: BreadcrumbItem[];
  };
  navBarData: NavBarContent;
  timelineData?: TimeLineApi;
}

export default function CompanyProfile({ responseData, navBarData, timelineData = null }: CompanyProfileProps) {
  const router = useRouter();
  const { empresa } = router.query;

  const { data, segmentacoes, schema, ssr, breadcrumb } = responseData;
  const { nome, fundacao, codigoBolsa, subsidiariaDe, imagem, links, texto, produtosEmpresa } = data;

  const [isMobile] = useBreakpoint('tablet');

  return (
    <Layout layoutData={{ navBarData, segmentacoes, tagsData: ssr, schema, breadcrumbList: breadcrumb }}>
      <WrapperContent isOnHeader hasBreadcrumb>
        <Breadcrumb breadcrumbList={breadcrumb} />
        <ProfilePage
          nome={nome}
          imagem={imagem}
          links={links}
          codigoBolsa={codigoBolsa}
          subsidiariaDe={subsidiariaDe}
          fundacao={fundacao}
          texto={texto}
          produtosEmpresa={produtosEmpresa}
          type="empresa"
        />
      </WrapperContent>
      {produtosEmpresa && produtosEmpresa.length > 0 && (
        <FeaturedProducts featuredProductsData={produtosEmpresa} title={`Produtos ${nome}`} shouldShowChip url={`/empresa/${empresa}/produtos/`} />
      )}
      <ProfileAdContainer>
        <AdsGeneratorMemo type={`${isMobile ? 'rectangle' : 'billboard'}/main/listing`} marginBottomP="24px" />
      </ProfileAdContainer>
      {timelineData && timelineData?.data?.timeline && (
        <FeaturedLastNews
          featuredLastNewsData={timelineData.data.timeline}
          rootPage={`empresa/${empresa}`}
          textTitle={`Últimas sobre ${nome}`}
          route={empresa as string}
          routeType="empresa"
        />
      )}
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { host } = context.req.headers;
  const { empresa } = context.query;

  const profileReq = { promise: site.get<CompanyProfileProps>(`/empresa/${empresa as string}/`), link: `/empresa/${empresa as string}/` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };
  const timelinesReq = {
    promise: mimir.get<TimeLineApi>(`/timelines/ultimas/empresa/${empresa as string}`),
    link: `/timelines/ultimas/empresa/${empresa as string}`,
  };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: profileReq,
    navBarPromise: responseMenuReq,
    timelinePromise: timelinesReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;
  const timelineData: TimeLineApi = promiseData[timelinesReq.link].data;

  const { headers } = data;
  const responseData = data;

  context.res.setHeader('Cache-Control', headers['Cache-control']);
  context.res.setHeader('Expires', headers.Expires);
  context.res.statusCode = statusCode;

  if (empresa === 'oppo') {
    return {
      redirect: {
        destination: '/oppo/',
        permanent: false,
      },
      props: {},
    };
  }

  return {
    props: {
      responseData,
      navBarData,
      timelineData,
      headers,
    },
  };
};
